import React, { useState, useEffect } from 'react';
import {
  Table, Button, Modal, Form, Input, Tag, Space,
  message, Tooltip, Popconfirm, Typography, Card, Row, Col
} from 'antd';
import {
  PlusOutlined, EditOutlined, DeleteOutlined, SettingOutlined,
  ShopOutlined, TagsOutlined
} from '@ant-design/icons';
import { useAuth } from '../../utils/auth';
import storeService from '../../services/storeService';
import EmployeeSelect from '../common/EmployeeSelect';
import TagSelect from '../common/TagSelect';

const { Text } = Typography;

/**
 * 门店配置表格组件
 * 用于批量活码创建时的门店选择和参数配置
 */
const StoreConfigTable = ({ value = [], onChange, loading, lbsContactWayName = '', onTagMapReady, disabledStoreIds = [] }) => {
  const { token } = useAuth();
  const [storeModalVisible, setStoreModalVisible] = useState(false);
  const [stores, setStores] = useState([]);
  const [tags, setTags] = useState([]);
  const [storesLoading, setStoresLoading] = useState(false);
  const [tagsLoading, setTagsLoading] = useState(false);

  // 初始化数据
  useEffect(() => {
    fetchStores();
  }, []);

  // 当LBS活码名称改变时，自动更新所有门店的活码名称
  useEffect(() => {
    if (value.length > 0) {
      const updatedConfigs = value.map(config => ({
        ...config,
        contactWayName: lbsContactWayName 
          ? `${lbsContactWayName}-${config.storeName}` 
          : `${config.storeName}活码`
      }));
      onChange(updatedConfigs);
    }
  }, [lbsContactWayName]); // 依赖LBS活码名称变化

  const fetchStores = async () => {
    try {
      setStoresLoading(true);
      const response = await storeService.getStores({ page: 0, size: 1000 });
      console.log('门店数据响应:', response);
      setStores(response.content || []);
    } catch (error) {
      console.error('获取门店列表失败:', error);
      message.error('获取门店列表失败');
    } finally {
      setStoresLoading(false);
    }
  };

  // 标签数据改由 TagSelect 内部加载

  /**
   * 更新配置项
   */
  const updateConfig = (index, field, newValue) => {
    const newConfigs = [...value];
    newConfigs[index] = { ...newConfigs[index], [field]: newValue };
    onChange(newConfigs);
  };

  // 表格列定义 - 直接可编辑版本
  const columns = [
    {
      title: '门店名称',
      dataIndex: 'storeName',
      width: 180,
      fixed: 'left',
      render: (text, record) => (
        <Space>
          <ShopOutlined />
          <Text strong>{text}</Text>
        </Space>
      )
    },
    {
      title: '活码名称',
      dataIndex: 'contactWayName',
      width: 200,
      render: (text, record, index) => (
        <Input
          placeholder="请输入活码名称"
          value={text}
          onChange={(e) => updateConfig(index, 'contactWayName', e.target.value)}
          size="middle"
          maxLength={30}
        />
      )
    },
    {
      title: '选择成员',
      dataIndex: 'userIds',
      width: 250,
      render: (userIds, record, index) => (
        <EmployeeSelect
          multiple
          placeholder="请选择成员"
          value={userIds || []}
          onChange={(values) => updateConfig(index, 'userIds', values)}
          width="100%"
          showDisplayModeSwitch={false}
          defaultDisplayMode="department"
        />
      )
    },
    {
      title: '标签',
      dataIndex: 'tags',
      width: 300,
      render: (selectedTags, record, index) => (
        <TagSelect
          value={selectedTags || []}
          onChange={(values) => updateConfig(index, 'tags', values)}
          onTagMapReady={onTagMapReady}
          width="100%"
          placeholder="选择标签"
          maxTagCount={null}
        />
      )
    },
    {
      title: '操作',
      width: 80,
      fixed: 'right',
      render: (_, record, index) => (
        <Popconfirm
          title="确定要删除这个门店吗？"
          onConfirm={() => handleDelete(index)}
        >
          <Button
            type="text"
            danger
            icon={<DeleteOutlined />}
            size="small"
          >
            删除
          </Button>
        </Popconfirm>
      )
    }
  ];

  /**
   * 添加门店
   */
  const handleAddStore = () => {
    setStoreModalVisible(true);
  };

  /**
   * 选择门店后的配置
   */
  const handleStoreSelected = (selectedStores) => {
    const newConfigs = selectedStores.map(store => {
      // 生成活码名称，如果有LBS活码名称前缀则使用，否则使用默认格式
      const contactWayName = lbsContactWayName 
        ? `${lbsContactWayName}-${store.name}` 
        : `${store.name}活码`;
      
      return {
        storeId: store.id,
        storeName: store.name,
        contactWayName,
        userIds: [],
        tags: []
      };
    });
    
    // 过滤已存在的门店
    const existingStoreIds = value.map(config => config.storeId);
    const filteredConfigs = newConfigs.filter(config => 
      !existingStoreIds.includes(config.storeId)
    );
    
    if (filteredConfigs.length === 0) {
      message.warning('选择的门店已存在');
      return;
    }
    
    onChange([...value, ...filteredConfigs]);
    message.success(`已添加 ${filteredConfigs.length} 个门店`);
    setStoreModalVisible(false);
  };

  /**
   * 删除门店配置
   */
  const handleDelete = (index) => {
    const newValue = [...value];
    newValue.splice(index, 1);
    onChange(newValue);
    message.success('删除成功');
  };

  return (
    <div>
      {/* 操作栏 */}
      <div style={{ marginBottom: 16 }}>
        <Space>
          <Button 
            type="primary" 
            icon={<PlusOutlined />} 
            onClick={handleAddStore}
          >
            添加门店
          </Button>
          {value.length > 0 && (
            <Text type="secondary">
              已配置 {value.length} 个门店
            </Text>
          )}
        </Space>
      </div>

      {/* 门店配置表格 */}
      <Table
        columns={columns}
        dataSource={value}
        rowKey={(record, index) => `${record.storeId}_${index}`}
        pagination={false}
        size="middle"
        loading={loading}
        scroll={{ x: 900 }}
        rowClassName={() => 'store-config-row'}
        locale={{
          emptyText: (
            <div style={{ padding: '40px 0', textAlign: 'center' }}>
              <ShopOutlined style={{ fontSize: 48, color: '#d9d9d9' }} />
              <p style={{ color: '#999', marginTop: 16 }}>
                请点击"添加门店"选择需要创建活码的门店
              </p>
              <p style={{ color: '#999', fontSize: 12 }}>
                添加门店后可直接在表格中编辑活码名称、选择成员和标签
              </p>
            </div>
          )
        }}
      />

      {/* 自定义样式 */}
      <style jsx>{`
        :global(.store-config-row) {
          height: 64px;
        }
        :global(.store-config-row .ant-table-cell) {
          padding: 12px 8px !important;
          vertical-align: middle;
        }
        :global(.store-config-row .ant-select) {
          min-height: 40px;
        }
        :global(.store-config-row .ant-input) {
          height: 40px;
        }
      `}</style>

      {/* 门店选择弹窗 */}
      <Modal
        title="选择门店"
        open={storeModalVisible}
        onCancel={() => setStoreModalVisible(false)}
        width={800}
        footer={null}
      >
        <StoreSelector 
          stores={stores}
          loading={storesLoading}
          onSelect={handleStoreSelected}
          selectedStoreIds={value.map(config => config.storeId)}
          disabledStoreIds={disabledStoreIds}
        />
      </Modal>

    </div>
  );
};

/**
 * 门店选择器组件
 */
const StoreSelector = ({ stores, loading, onSelect, selectedStoreIds, disabledStoreIds = [] }) => {
  const [selectedRows, setSelectedRows] = useState([]);
  const [searchText, setSearchText] = useState('');

  // 过滤门店
  const filteredStores = stores.filter(store =>
    store.name.toLowerCase().includes(searchText.toLowerCase()) ||
    store.address?.toLowerCase().includes(searchText.toLowerCase())
  );

  // 表格列定义
  const columns = [
    {
      title: '门店名称',
      dataIndex: 'name',
      render: (text, record) => (
        <Space>
          <ShopOutlined />
          <div>
            <div>{text}</div>
            {record.address && (
              <Text type="secondary" style={{ fontSize: '12px' }}>
                {record.address}
              </Text>
            )}
          </div>
        </Space>
      )
    }
    // {
    //   title: '服务半径',
    //   dataIndex: 'serviceRadius',
    //   width: 120,
    //   render: (radius) => radius ? `${radius}m` : '-'
    // },
    // {
    //   title: '状态',
    //   dataIndex: 'status',
    //   width: 100,
    //   render: (status) => (
    //     <Tag color={status === 'active' ? 'green' : 'default'}>
    //       {status === 'active' ? '启用' : '停用'}
    //     </Tag>
    //   )
    // }
  ];

  const preselectedDisabledIds = Array.from(new Set([...(selectedStoreIds || []), ...(disabledStoreIds || [])]));
  const allSelectedRowKeys = Array.from(new Set([...preselectedDisabledIds, ...selectedRows.map(row => row.id)]));

  const rowSelection = {
    selectedRowKeys: allSelectedRowKeys,
    onChange: (selectedRowKeys, selectedRowsParam) => {
      const activeRows = selectedRowsParam.filter(row => !preselectedDisabledIds.includes(row.id));
      setSelectedRows(activeRows);
    },
    getCheckboxProps: (record) => ({
      disabled: preselectedDisabledIds.includes(record.id),
      name: record.name,
    }),
  };

  return (
    <div>
      <div style={{ marginBottom: 16 }}>
        <Input.Search
          placeholder="搜索门店名称或地址"
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          style={{ width: 300 }}
        />
      </div>

      <Table
        rowSelection={rowSelection}
        columns={columns}
        dataSource={filteredStores}
        rowKey="id"
        loading={loading}
        pagination={{ pageSize: 100 }}
        size="small"
      />

      <div style={{ marginTop: 16, textAlign: 'right' }}>
        <Space>
          <Text>已选择 {selectedRows.length} 个门店</Text>
          <Button
            type="primary"
            onClick={() => onSelect(selectedRows)}
            disabled={selectedRows.length === 0}
          >
            确认添加
          </Button>
        </Space>
      </div>
    </div>
  );
};

export default StoreConfigTable;